import { RefObject, useCallback, useLayoutEffect, useRef } from 'react';

export const useHorizontalScroll = (
  ref: RefObject<HTMLElement>,
  restriction?: 'onlyWheel' | 'onlyDrag'
) => {
  const dragAbortController = useRef<AbortController | null>();
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const startX = useRef<number>(0);
  const startCursor = useRef<string>('');
  const scrollLeft = useRef<number>(0);

  useLayoutEffect(() => {
    const scrollContainer = ref.current;

    if (!scrollContainer) {
      return;
    }

    resizeObserverRef.current?.disconnect();

    resizeObserverRef.current = new ResizeObserver(() => {
      const { scrollWidth, clientWidth, scrollHeight, clientHeight } =
        scrollContainer;

      if (scrollWidth > clientWidth) {
        restriction !== 'onlyWheel' &&
          scrollContainer.addEventListener('mousedown', dragStart, {
            signal: dragAbortController.current?.signal,
          });

        startCursor.current = scrollContainer.style.cursor;

        if (restriction !== 'onlyDrag' && scrollHeight <= clientHeight) {
          scrollContainer.addEventListener('wheel', handleWheelScroll);
        } else {
          scrollContainer.removeEventListener('wheel', handleWheelScroll);
        }
      } else {
        dragAbortController.current?.abort();
        scrollContainer.removeEventListener('wheel', handleWheelScroll);
      }
    });

    resizeObserverRef.current?.observe(scrollContainer);

    return () => {
      resizeObserverRef.current?.disconnect();
      dragAbortController.current?.abort();
      scrollContainer.removeEventListener('wheel', handleWheelScroll);
    };
  }, [
    ref,
    restriction,
    ref.current?.scrollWidth,
    ref.current?.clientWidth,
    ref.current?.scrollHeight,
    ref.current?.clientHeight,
  ]);

  const drag = (e: MouseEvent): void => {
    if (!ref.current) {
      return;
    }

    if (Math.abs(startX.current - e.clientX) >= 10) {
      ref.current.style.pointerEvents = 'none';
      ref.current.style.cursor = 'grab';
      ref.current.style.userSelect = 'none';
      ref.current.scrollLeft = scrollLeft.current + startX.current - e.clientX;
    }
  };

  const dragEnd = (e: MouseEvent): void => {
    if (!ref.current) {
      return;
    }

    startX.current = e.clientX;
    ref.current.style.cursor = startCursor.current;
    ref.current.style.userSelect = 'auto';
    dragAbortController.current?.abort();
    ref.current.style.pointerEvents = 'auto';
  };

  const dragStart = useCallback(
    (e: MouseEvent): void => {
      if (!ref.current) {
        return;
      }

      if (ref.current.scrollWidth > ref.current.clientWidth) {
        startX.current = e.clientX;
        startCursor.current = ref.current.style.cursor;
        scrollLeft.current = ref.current.scrollLeft;

        dragAbortController.current = new AbortController();
        const signal = dragAbortController.current.signal;

        ref.current.addEventListener('mousemove', drag, {
          signal,
        });
        document.addEventListener('mouseup', dragEnd, {
          signal,
        });
      }
    },
    [ref]
  );

  const handleScroll = (direction: 'left' | 'right'): void => {
    if (!ref.current) {
      return;
    }

    ref.current.scrollLeft += direction === 'left' ? -50 : 50;
  };

  const handleWheelScroll = useCallback((e: WheelEvent): void => {
    e.preventDefault();
    const direction: number = e.deltaY;
    handleScroll(direction > 0 ? 'right' : 'left');
  }, []);
};
